import { useMediaQuery } from "@mui/material";
import Cookies from "js-cookie";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendTrackingEvent } from "../../helpers/tracking_management";
import {
  getCompany,
  getCompanyAffiliates,
  getCompanyJobs,
  getDiscoverCompanies,
  updateSelectedCompany,
} from "../../redux/actions/company_action";
import { getSpecialisation } from "../../redux/actions/job_action";
import SharedToast from "../shared/SharedToast/SharedToast";
import CompanyAddress from "./CompanyAddress/CompanyAddress";
import CompanyAffiliates from "./CompanyAffiliates/CompanyAffiliates";
import CompanyBenefits from "./CompanyBenefits/CompanyBenefits";
import CompanyDescription from "./CompanyDescription/CompanyDescription";
import CompanyExtras from "./CompanyExtras/CompanyExtras";
import CompanyJobs from "./CompanyJobs/CompanyJobs";
import CompanyNavigation from "./CompanyNavigation/CompanyNavigation";
import CompanyPictures from "./CompanyPictures/CompanyPictures";
import CompanySocialMedia from "./CompanySocialMedia/CompanySocialMedia";
import CompanyVideos from "./CompanyVideos/CompanyVideos";
import DiscoverCompany from "./DiscoverCompany/DiscoverCompany";
import {
  DividerStyled,
  FlexContainer,
  InnerWrapper,
  OuterWrapper,
  SocialMediaContainer,
} from "./styles";

function CompanyWrapper(props) {
  const { company } = props || {};

  const dispatch = useDispatch();

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [currentTab, setCurrentTab] = useState(1);

  const companyId = company?.id;

  const discoverCompaniesParams = {
    companyId: companyId,
    industryId: company.industryId,
    companySize: company.sizeId,
  };

  const companySocial = company?.profile?.socialMediaLinks || [];

  const companySocialExist = companySocial.length > 0;

  const companyPictures =
    {
      id: company?.id,
      name: company?.name,
      images: company?.profile?.images,
    } || {};

  const companyVideos =
    {
      name: company?.name,
      videos: company?.profile?.videos,
    } || {};

  const companyVideoExist = companyVideos?.videos.length > 0;

  const companyExtras = company?.profile?.extras || [];

  const companyExtrasExist = companyExtras?.length > 0;

  const companyAddress =
    {
      address: company?.profile?.address,
      latitude: company?.profile?.latitude,
      longitude: company?.profile?.longitude,
    } || {};

  const companyAffiliates =
    {
      name: company?.name,
      id: company?.id,
      affiliates: company?.affiliatedCompany ?? company?.companyAffiliation,
    } || {};

  const companyAffiliatesExist =
    companyAffiliates?.affiliates.length > 0 ||
    !Array.isArray(companyAffiliates?.affiliates);

  const companyDetail = {
    id: company?.id,
    name: company?.name,
    location: company?.location,
    industry: company?.industry,
    industryId: company?.industryId,
    jobCount: company?.jobCount,
    videoCount: company?.profile?.videos?.length,
    benefitsCount: company?.benefits?.length,
    sizeId: company?.sizeId,
    logo: company?.logo,
    slug: company?.slug,
    bookmark: company?.bookmark,
    category: company?.category,
    affiliatesExist: companyAffiliatesExist,
    extrasExist: companyExtrasExist,
    recruitmentFirm: company?.recruitmentFirm,
  };

  const discoverCompanies = useSelector(
    (state) => state.companies.cpDiscoverMoreCompanies
  );

  const filteredDiscoverCompanies = discoverCompanies?.filter(
    (company) => company?.company?.id !== companyDetail?.id
  );

  const companyAffliates = useSelector(
    (state) => state.companies.companyAffiliates.company
  );

  useEffect(() => {
    dispatch(updateSelectedCompany(company));
    dispatch(getCompany({ companyId: companyId }));
    dispatch(getDiscoverCompanies(discoverCompaniesParams));
    dispatch(getCompanyAffiliates({ companyId: companyAffiliates?.id }));

    if (company?.jobCount > 0) {
      dispatch(getSpecialisation());
      dispatch(getCompanyJobs({ companyId: companyId }));
    }

    sendTrackingEvent({
      event: "CE_view-company",
      "company-id": company?.id ?? null,
    });

    setTimeout(() => {
      Cookies.set("click-source", null);
    }, 30000);
  }, [companyId, dispatch, company]);

  const companyAffiliatesFromRedux = useMemo(() => {
    return (
      companyAffliates?.affiliatedCompany ??
      companyAffliates?.companyAffiliation
    );
  }, [companyAffliates]);

  const filteredCompany = Array.isArray(companyAffiliatesFromRedux)
    ? companyAffiliatesFromRedux?.filter(
        (company) => company?.affiliatedCompany?.hide === false
      )
    : companyAffiliatesFromRedux;

  return (
    <OuterWrapper>
      <SharedToast />
      {/* Company Navigation */}
      <CompanyNavigation
        companyDetail={companyDetail}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
        companySocialExist={companySocialExist}
      />

      {/* Company Description with Social Media */}
      {companySocialExist ? (
        <SocialMediaContainer>
          <FlexContainer
            flexDirection={isMobile ? "column-reverse" : "row"}
            justifyContent={"space-between"}
            gap={"2.4375rem"}
            padding={isMobile ? "0" : "2rem 0 0"}
            overflow={"clip"}
          >
            {!isMobile ? (
              <CompanyDescription
                companyDetail={company?.profile?.descriptions}
                companySocial={companySocial}
                setCurrentTab={setCurrentTab}
              />
            ) : null}
            <CompanySocialMedia companyDetail={companySocial} />
          </FlexContainer>
          <DividerStyled />
        </SocialMediaContainer>
      ) : null}

      {/* Company Pictures */}
      {companyPictures?.images.length > 0 && (
        <>
          <CompanyPictures
            companyDetail={companyPictures}
            setCurrentTab={setCurrentTab}
          />
          <DividerStyled />
        </>
      )}

      <InnerWrapper>
        {/* Company Description */}
        {!companySocialExist || !isDesktop ? (
          <>
            <CompanyDescription
              companyDetail={company?.profile?.descriptions}
              companySocial={companySocial}
              setCurrentTab={setCurrentTab}
            />

            <DividerStyled />
          </>
        ) : null}

        {/* Company Media Videos */}
        {companyVideoExist ? (
          <>
            <CompanyVideos
              companyDetail={companyVideos}
              setCurrentTab={setCurrentTab}
            />

            <DividerStyled />
          </>
        ) : null}

        {/* Company Benefits */}
        {company?.benefits?.length > 0 ? (
          <>
            <CompanyBenefits
              companyDetail={company?.benefits}
              setCurrentTab={setCurrentTab}
            />
            <DividerStyled />
          </>
        ) : null}

        {/* Company Extras */}
        {companyExtrasExist ? (
          <>
            <CompanyExtras companyDetail={companyExtras} />
            <DividerStyled />
          </>
        ) : null}

        {/* Company Address */}
        <CompanyAddress companyDetail={companyAddress} />
        {(filteredDiscoverCompanies?.length > 0 ||
          company?.jobCount > 0 ||
          companyAffiliatesExist) && <DividerStyled />}

        {/* Company Affiliates */}
        {companyAffiliatesExist && !isEmpty(filteredCompany) && (
          <>
            <CompanyAffiliates companyAffiliates={filteredCompany} />
            <DividerStyled />
          </>
        )}

        {/* Company Job List */}
        {company?.jobCount > 0 ? (
          <>
            <CompanyJobs setCurrentTab={setCurrentTab} />
            <DividerStyled />
          </>
        ) : null}

        {/* Discover Company */}
        {filteredDiscoverCompanies?.length > 0 && (
          <>
            <DiscoverCompany companyDetail={companyDetail} />
          </>
        )}
      </InnerWrapper>
    </OuterWrapper>
  );
}

export default CompanyWrapper;
